/**
 * A small search box that live-filters a long facet checkbox list (e.g. Brands).
 * - Case-insensitive substring match on the option label.
 * - Checked options always remain visible, even when they don't match.
 * - Clearing the box restores the full list.
 * - Survives facet section re-renders: the current term is kept per list id and
 *   re-applied when the component reconnects or the list is rebuilt.
 */
const facetSearchTerms = (window.__facetSearchTerms = window.__facetSearchTerms || {});

class FacetSearchComponent extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.input = this.querySelector('input');
    this.emptyMessage = this.querySelector('[data-facet-search-empty]');

    if (!this.input) return;

    // Restore the term if this component was rebuilt by a section re-render.
    const savedTerm = facetSearchTerms[this.listId];
    if (savedTerm && this.input.value === '') {
      this.input.value = savedTerm;
    }

    this.input.addEventListener('input', () => {
      facetSearchTerms[this.listId] = this.input.value;
      this.applyFilter();
    });

    // Facet lists are re-rendered when filters change; re-apply the search term.
    const list = this.list;
    if (list) {
      this.#observer = new MutationObserver(() => this.applyFilter());
      this.#observer.observe(list, { childList: true, subtree: true });
    }

    // Apply once on (re)connect so stale row visibility is always corrected.
    requestAnimationFrame(() => this.applyFilter());
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get listId() {
    return this.input?.getAttribute('data-list') || '';
  }

  get list() {
    const id = this.listId;
    return id ? document.getElementById(id) : null;
  }

  applyFilter() {
    const list = this.list;
    if (!list || !this.input) return;

    const term = this.input.value.trim().toLowerCase();
    let visibleCount = 0;

    for (const item of list.querySelectorAll('.facets__inputs-list-item')) {
      const checkbox = item.querySelector('input[type="checkbox"]');
      const label = (checkbox?.getAttribute('data-label') || item.textContent || '').trim().toLowerCase();
      const isChecked = checkbox instanceof HTMLInputElement && checkbox.checked;
      const matches = term === '' || label.includes(term) || isChecked;

      item.style.display = matches ? '' : 'none';
      if (matches) visibleCount += 1;
    }

    if (this.emptyMessage instanceof HTMLElement) {
      this.emptyMessage.hidden = !(term !== '' && visibleCount === 0);
    }
  }
}

if (!customElements.get('facet-search-component')) {
  customElements.define('facet-search-component', FacetSearchComponent);
}
